import React from 'react';

const WWIIPage = () => {
  const timeline = [
    { year: '1939', event: 'Germany Invades Poland', description: 'Hitler\'s invasion of Poland on September 1 triggers Britain and France to declare war, beginning WWII.' },
    { year: '1940', event: 'Fall of France', description: 'Germany conquers France in just six weeks, forcing the evacuation of Allied troops at Dunkirk.' },
    { year: '1940', event: 'Battle of Britain', description: 'The RAF successfully defends Britain against the German Luftwaffe in the first major air campaign.' },
    { year: '1941', event: 'Operation Barbarossa', description: 'Germany launches a massive invasion of the Soviet Union, the largest military operation in history.' },
    { year: '1941', event: 'Pearl Harbor Attack', description: 'Japan\'s surprise attack on Pearl Harbor brings the United States into the war.' },
    { year: '1942', event: 'Battle of Midway', description: 'A decisive American naval victory that turns the tide in the Pacific Theater.' },
    { year: '1942-43', event: 'Battle of Stalingrad', description: 'The bloodiest battle in history marks the beginning of Germany\'s retreat on the Eastern Front.' },
    { year: '1944', event: 'D-Day Normandy Invasion', description: 'Allied forces land in France, opening a Western Front and beginning the liberation of Europe.' },
    { year: '1945', event: 'Fall of Berlin', description: 'Soviet forces capture Berlin; Hitler commits suicide; Germany surrenders on May 8 (V-E Day).' },
    { year: '1945', event: 'Atomic Bombs & Japan Surrenders', description: 'The U.S. drops atomic bombs on Hiroshima and Nagasaki; Japan surrenders on August 15 (V-J Day).' }
  ];

  const keyFacts = [
    { icon: '☠️', title: 'Deadliest Conflict', fact: 'Over 70-85 million deaths, approximately 3% of the world\'s population at the time' },
    { icon: '🌍', title: 'Truly Global', fact: 'Involved over 100 million military personnel from more than 30 countries across six continents' },
    { icon: '☢️', title: 'Nuclear Age', fact: 'First and only use of nuclear weapons in warfare - Hiroshima and Nagasaki' },
    { icon: '🛑', title: 'The Holocaust', fact: 'Systematic genocide of 6 million Jews and millions of other victims by Nazi Germany' },
    { icon: '🏙️', title: 'Cities Destroyed', fact: 'Entire cities reduced to rubble, including Dresden, Hiroshima, Tokyo, and Warsaw' },
    { icon: '🌐', title: 'New World Order', fact: 'Led to the creation of the United Nations and the beginning of the Cold War' }
  ];

  return (
    <div className="min-h-screen bg-stone-50" data-testid="wwii-page">
      {/* Hero Section */}
      <div 
        className="relative h-96 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1573677394729-54f076176265?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHx3b3JsZCUyMHdhciUyMHR3b3xlbnwwfHx8fDE3NzYxNTgwMDJ8MA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="hero-overlay absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-6">
            <h1 className="text-6xl md:text-7xl font-bold mb-4 text-amber-400" data-testid="page-title">
              World War II
            </h1>
            <p className="text-2xl text-amber-100 font-semibold">1939 - 1945</p>
            <p className="text-xl text-amber-200 mt-4 italic">"The Deadliest Conflict in Human History"</p>
          </div>
        </div>
      </div>

      {/* Overview Section */}
      <div className="container mx-auto px-6 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-stone-800 mb-6" data-testid="overview-title">Overview</h2>
          <div className="section-divider"></div>
          <p className="text-xl text-stone-700 leading-relaxed mb-6">
            World War II was a global conflict that lasted from 1939 to 1945, involving the vast majority of the world\'s nations. The war was fought between two major alliances: the Axis powers (primarily Nazi Germany, Imperial Japan, and Fascist Italy) and the Allied powers (led by Great Britain, the Soviet Union, the United States, and China).
          </p>
          <p className="text-xl text-stone-700 leading-relaxed mb-6">
            The war was characterized by unprecedented destruction, the Holocaust, strategic bombing of civilian populations, the development and use of nuclear weapons, and total war that blurred the lines between military and civilian targets. It fundamentally reshaped the global political landscape, leading to the emergence of the United States and Soviet Union as superpowers and the beginning of the Cold War.
          </p>
          <p className="text-xl text-stone-700 leading-relaxed">
            World War II resulted in an estimated 70-85 million fatalities, making it the deadliest military conflict in human history. The war\'s end brought about the establishment of the United Nations, decolonization movements, and a new international order that continues to influence global politics today.
          </p>
        </div>
      </div>

      {/* Timeline Section */}
      <div className="bg-stone-100 py-16">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-stone-800 mb-12 text-center" data-testid="timeline-title">
            Timeline of Major Events
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="timeline-line pl-12 space-y-8">
              {timeline.map((item, index) => (
                <div key={index} className="relative" data-testid={`timeline-event-${index}`}>
                  <div className="timeline-dot"></div>
                  <div className="bg-white p-6 rounded-lg shadow-lg">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-2xl font-bold text-amber-600">{item.year}</h3>
                    </div>
                    <h4 className="text-xl font-bold text-stone-800 mb-2">{item.event}</h4>
                    <p className="text-stone-600 text-lg leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Key Facts Section */}
      <div className="container mx-auto px-6 py-16">
        <h2 className="text-4xl font-bold text-stone-800 mb-12 text-center" data-testid="facts-title">
          Key Facts & Figures
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {keyFacts.map((fact, index) => (
            <div 
              key={index} 
              className="bg-white p-6 rounded-lg shadow-lg historical-border hover:shadow-xl transition-shadow"
              data-testid={`fact-card-${index}`}
            >
              <div className="text-4xl mb-4">{fact.icon}</div>
              <h3 className="text-xl font-bold text-amber-600 mb-3">{fact.title}</h3>
              <p className="text-stone-700 text-lg leading-relaxed">{fact.fact}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Causes & Consequences */}
      <div className="bg-stone-100 py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-stone-800 mb-6" data-testid="causes-title">Main Causes</h2>
              <div className="section-divider"></div>
              <ul className="space-y-4 mt-6">
                <li className="flex items-start">
                  <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                  <span className="text-lg text-stone-700">The harsh Treaty of Versailles and its economic impact on Germany</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                  <span className="text-lg text-stone-700">Rise of fascism and totalitarian regimes in Germany, Italy, and Japan</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                  <span className="text-lg text-stone-700">Failure of appeasement policies and the League of Nations</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                  <span className="text-lg text-stone-700">German expansionism and the invasion of Poland in 1939</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                  <span className="text-lg text-stone-700">Japanese imperialism in Asia and the Pacific</span>
                </li>
              </ul>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-stone-800 mb-6" data-testid="consequences-title">Consequences</h2>
              <div className="section-divider"></div>
              <ul className="space-y-4 mt-6">
                <li className="flex items-start">
                  <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                  <span className="text-lg text-stone-700">Emergence of USA and USSR as global superpowers</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                  <span className="text-lg text-stone-700">Beginning of the Cold War and division of Europe</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                  <span className="text-lg text-stone-700">Creation of the United Nations and modern international law</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                  <span className="text-lg text-stone-700">Decolonization movements across Africa and Asia</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-600 font-bold text-xl mr-3">•</span>
                  <span className="text-lg text-stone-700">Nuclear age and arms race between superpowers</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Theater of War */}
      <div className="container mx-auto px-6 py-16">
        <h2 className="text-4xl font-bold text-stone-800 mb-12 text-center">Major Theaters of War</h2>
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-2xl font-bold text-amber-600 mb-4">European Theater</h3>
            <p className="text-stone-700 text-lg leading-relaxed">
              The struggle against Nazi Germany across Europe, from the Battle of Britain to the liberation of concentration camps and the fall of Berlin.
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-2xl font-bold text-amber-600 mb-4">Pacific Theater</h3>
            <p className="text-stone-700 text-lg leading-relaxed">
              Island-hopping campaigns, naval battles, and ultimately the atomic bombings that ended Imperial Japan\'s expansion across Asia and the Pacific.
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-2xl font-bold text-amber-600 mb-4">Eastern Front</h3>
            <p className="text-stone-700 text-lg leading-relaxed">
              The massive and brutal conflict between Nazi Germany and the Soviet Union, including the Siege of Leningrad and Battle of Stalingrad.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WWIIPage;